import { m } from 'framer-motion';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// routes
import { useRouter } from 'src/routes/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify/iconify';
import { Colors } from 'src/theme/palette';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Button, CircularProgress, Dialog, TextField } from '@mui/material';
import TruxFormField from 'src/components/inputs/trux-form-field';
import axios, { endpoints } from 'src/utils/axios';
import { paths } from 'src/routes/paths';



export default function AccountPopover() {
  const router = useRouter();

  const { logout, session, passwordUpdated } = useAuthContext();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  const [passwordChange, setPasswordChange] = useState(false);
  const [loading, setLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [repeatPassword, setRepeatPassword] = useState("")

  const handlePasswordChange = () => {
    if (currentPassword === "" || newPassword === "" || repeatPassword === "") {
      enqueueSnackbar("All fields are required.", { variant: "error" });
      return;
    }
    if (newPassword !== repeatPassword) {
      enqueueSnackbar("Passwords do not match.", { variant: "error" });
      return;
    }
    if (currentPassword === newPassword) {
      enqueueSnackbar("Old and new passwords are the same.", { variant: "error" });
      return;
    }

    setLoading(true);
    axios.put(endpoints.users.updatePassword, {
      currentPassword,
      newPassword,
    }).then((response) => {
      passwordUpdated(response.data.accessToken, response.data.session);

      enqueueSnackbar("Successfully updated password.");
      setPasswordChange(false);
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
    }).catch(e => {
      if (e.response?.status === 422) {
        e.response.data?.map((l: any) => enqueueSnackbar(l.message, {variant: "error"}));
      } else {
        enqueueSnackbar("Unknown error has occured.", {variant: "error"});
      }
    }).finally(() => {
      setLoading(false);
    });
  }
  const authContext = useAuthContext();
  const isOwner = authContext?.session?.organization?.ownerId === authContext?.session?.user?.id;

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={popover.open ? 'inherit' : 'default'}
        onClick={popover.onOpen}
        sx={{
          ...(popover.open && {
            bgcolor: (theme) => theme.palette.action.selected,
          }),
        }}
      >
        <Iconify width={24} icon="solar:user-rounded-bold-duotone" color={Colors.greyText} />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {session?.user?.name}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {session?.user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={() => setPasswordChange(true)}
          sx={{ m: 1, fontWeight: '500', color: 'inherit' }}
        >
          Change Password
        </MenuItem>
        {isOwner && <MenuItem onClick={() => {
          router.push(paths.app.eldUsers.list);
          popover.setOpen(null);
        }}
          sx={{ m: 1, fontWeight: '500', color: 'inherit' }}
        >
          Manage Users
        </MenuItem>}
        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>
      <Dialog open={passwordChange} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3, minHeight: 300, display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Change Password
          </Typography>

          <Stack spacing={3}>
            <TruxFormField label="Current Password *">
              <TextField
                type="password"
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="new-password"
                size="small"
                label="Current Password"
                fullWidth
              />
            </TruxFormField>

            <TruxFormField label="New Password *">
              <TextField
                type="password"
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                size="small"
                label="New Password"
                fullWidth
              />
            </TruxFormField>

            <TruxFormField label="Repeat Password *">
              <TextField
                type="password"
                onChange={(e) => setRepeatPassword(e.target.value)}
                autoComplete="new-password"
                size="small"
                label="Repeat Password"
                fullWidth
              />
            </TruxFormField>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
            <Button onClick={() => setPasswordChange(false)} color="inherit">
              Cancel
            </Button>
            {loading && <Box display="flex" padding="10px" justifyContent="flex-end" overflow="hidden" margin="0 50px">
              <CircularProgress size={20} />
            </Box>}
            {!loading &&
            <Button variant="contained" onClick={() => handlePasswordChange()}>
              Change Password
            </Button>}
          </Stack>
        </Box>
      </Dialog>
    </>
  );
}
